import React, { useMemo } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

type BedroomMarketRow = {
  bedrooms?: number | string;
  median?: number | null;
  average?: number | null;
  medianPerSqFt?: number | null;
  averagePerSqFt?: number | null;
  medianDaysOnMarket?: number | null;
  averageDaysOnMarket?: number | null;
  totalListings?: number | null;
};

const money = (value: number) => `$${Math.round(value).toLocaleString()}`;
const compactMoney = (value: number) =>
  Math.abs(value) >= 1000 ? `$${Math.round(value / 1000).toLocaleString()}K` : money(value);

const SaleBedroomAnalysisCard: React.FC<{ rows?: BedroomMarketRow[] }> = ({ rows }) => {
  const data = useMemo(
    () =>
      (rows || [])
        .filter((row) => row.bedrooms != null && row.bedrooms !== '')
        .map((row) => ({
          label: `${row.bedrooms} ${Number(row.bedrooms) === 1 ? 'Bed' : 'Beds'}`,
          bedrooms: row.bedrooms,
          median: Number(row.median) || 0,
          average: Number(row.average) || 0,
          perSqFt: Number(row.medianPerSqFt ?? row.averagePerSqFt) || 0,
          dom: Number(row.medianDaysOnMarket ?? row.averageDaysOnMarket) || 0,
          listings: Number(row.totalListings) || 0,
        }))
        .sort((a, b) => Number(a.bedrooms) - Number(b.bedrooms)),
    [rows],
  );

  if (!data.length) return null;

  const reliable = data.filter((row) => row.median > 0 && row.listings > 1);
  const lowestCost = [...(reliable.length ? reliable : data.filter((row) => row.median > 0))]
    .sort((a, b) => a.median - b.median)[0];
  const deepestMarket = [...data].sort((a, b) => b.listings - a.listings)[0];

  return (
    <div
      style={{
        border: '1px solid #e8eef3',
        borderRadius: 14,
        background: '#fff',
        boxShadow: '0 2px 10px rgba(15, 23, 42, 0.06)',
        padding: 16,
        marginBottom: 16,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          color: '#1686a4',
          fontSize: 18,
          fontWeight: 800,
          marginBottom: 14,
        }}
      >
        <span
          style={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: '#dff4f8',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 16,
          }}
        >
          ↗
        </span>
        Sale Data by Bedrooms
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(180px, 220px)',
          gap: 16,
          alignItems: 'stretch',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#17233d' }}>
                Median vs Average Sale Price by Bedrooms
              </div>
              <div style={{ fontSize: 11, color: '#64748b' }}>Sale Price (USD)</div>
            </div>
            <div
              style={{
                alignSelf: 'flex-start',
                borderRadius: 5,
                background: '#edf4ff',
                color: '#3569a7',
                padding: '5px 9px',
                fontSize: 10,
              }}
            >
              ⓘ Prices in USD
            </div>
          </div>

          <div style={{ display: 'flex', gap: 18, fontSize: 11, color: '#475569', margin: '8px 0 0' }}>
            <span><span style={{ color: '#2778dd', fontSize: 17 }}>■</span> Median Sale Price</span>
            <span><span style={{ color: '#12a3aa', fontSize: 17 }}>●</span> Average Sale Price</span>
          </div>

          <div style={{ width: '100%', height: 285 }}>
            <ResponsiveContainer>
              <ComposedChart data={data} margin={{ top: 28, right: 24, left: 8, bottom: 6 }}>
                <CartesianGrid stroke="#e7edf2" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#475569' }} axisLine={{ stroke: '#cbd5e1' }} />
                <YAxis
                  tickFormatter={compactMoney}
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  axisLine={false}
                  tickLine={false}
                  width={62}
                />
                <Tooltip formatter={(value: number) => money(Number(value))} />
                <Bar dataKey="median" fill="#2778dd" radius={[3, 3, 0, 0]} maxBarSize={48}>
                  <LabelList
                    dataKey="median"
                    position="top"
                    formatter={(value: number) => money(Number(value))}
                    style={{ fill: '#173d87', fontSize: 10, fontWeight: 700 }}
                  />
                </Bar>
                <Line
                  dataKey="average"
                  type="monotone"
                  stroke="#12a3aa"
                  strokeWidth={2}
                  dot={{ r: 4, fill: '#12a3aa', stroke: '#fff', strokeWidth: 1 }}
                >
                  <LabelList
                    dataKey="average"
                    position="top"
                    offset={10}
                    formatter={(value: number) => money(Number(value))}
                    style={{ fill: '#1098a0', fontSize: 10, fontWeight: 700 }}
                  />
                </Line>
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div
            style={{
              display: 'flex',
              gap: 24,
              overflowX: 'auto',
              paddingBottom: 6,
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {data.map((row) => (
              <div
                key={row.label}
                style={{
                  border: '1px solid #e5eaf0',
                  borderRadius: 8,
                  padding: 7,
                  textAlign: 'center',
                  minWidth: 118,
                  flex: '0 0 118px',
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 700, color: '#17233d', marginBottom: 5 }}>{row.label}</div>
                {row.listings <= 1 && (
                  <div
                    style={{
                      background: '#fdebea',
                      color: '#be3428',
                      borderRadius: 5,
                      padding: '3px 2px',
                      fontSize: 9,
                      lineHeight: 1.25,
                      marginBottom: 5,
                    }}
                  >
                    ! Low confidence:<br />only {row.listings} listing
                  </div>
                )}
                <div style={{ background: '#eaf8f8', color: '#167c83', borderRadius: 5, padding: '3px 2px', fontSize: 10 }}>
                  ◇ {row.perSqFt ? `$${row.perSqFt.toFixed(2)} / sq ft` : '— / sq ft'}
                </div>
                <div style={{ background: '#edf4ff', color: '#3569a7', borderRadius: 5, padding: '3px 2px', fontSize: 10, marginTop: 4 }}>
                  ◷ {row.dom ? `${Math.round(row.dom)} days (DOM)` : '— DOM'}
                </div>
                <div style={{ background: '#eaf8f4', color: '#15806d', borderRadius: 5, padding: '3px 2px', fontSize: 10, marginTop: 4 }}>
                  ▤ {row.listings} {row.listings === 1 ? 'listing' : 'listings'}
                </div>
              </div>
            ))}
          </div>
        </div>

        <aside
          style={{
            borderRadius: 12,
            border: '1px solid #e3eaf1',
            background: 'linear-gradient(180deg, #f7fbff 0%, #fbfdff 100%)',
            color: '#17233d',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            padding: 18,
          }}
        >
          <div>
            <div
              style={{
                width: 58,
                height: 58,
                borderRadius: '50%',
                background: '#2778dd',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 12px',
                fontSize: 28,
              }}
            >
              ♧
            </div>
            <div style={{ color: '#17233d', fontSize: 17, fontWeight: 800 }}>Key Insight</div>
            <div style={{ width: 36, height: 3, borderRadius: 2, background: '#2778dd', margin: '10px auto 16px' }} />
            {lowestCost && (
              <div style={{ display: 'flex', gap: 7, textAlign: 'left', fontSize: 13, lineHeight: 1.55, marginBottom: 18 }}>
                <span style={{ color: '#2778dd', fontSize: 18 }}>●</span>
                <span>{lowestCost.label} homes are the lowest median-cost entry point at {money(lowestCost.median)}.</span>
              </div>
            )}
            {deepestMarket && (
              <div style={{ display: 'flex', gap: 7, textAlign: 'left', fontSize: 13, lineHeight: 1.55 }}>
                <span style={{ color: '#2778dd', fontSize: 18 }}>●</span>
                <span>{deepestMarket.label} homes offer the deepest bedroom market with {deepestMarket.listings} listings.</span>
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
};

export default SaleBedroomAnalysisCard;
